/**
 * A lab's side of saving: its list of the teacher's setups, which one is on
 * the bench, and the save itself. Knows nothing about any lab's payload — the
 * lab hands it one, and gets one back when a setup is picked.
 */
import { useCallback, useEffect, useState } from 'react';
import type { labSetups as L } from '@padcanvas/shared';
import type { LabSaving } from './types';

export interface CurrentSetup<P> {
    id: string;
    name: string;
    notes?: string;
    chapterIds?: string[];
    /** The payload as it was last saved or loaded. The lab compares what it
     *  has with this to say whether there are unsaved changes. */
    saved: P;
}

export interface SaveRequest<P> {
    payload: P;
    /** Needed for a new setup; a rename when saving over one. */
    name?: string;
    notes?: string;
    /** A new setup even when one is open. */
    asNew?: boolean;
    basedOn?: L.LabSetupBasis;
    chapterIds?: string[];
}

export interface LabSetupsState<P> {
    /** Whether the host lets this lab save at all. */
    enabled: boolean;
    mine: L.LabSetupSummary[];
    current: CurrentSetup<P> | null;
    busy: boolean;
    error: string | null;
    save(req: SaveRequest<P>): Promise<boolean>;
    /** Read one of the teacher's setups; the lab puts its payload on the bench. */
    load(id: string): Promise<P | null>;
    /** The bench has left the saved setup — for a preset, or a cleared bench. */
    leave(): void;
}

const message = (err: unknown): string => (err instanceof Error && err.message ? err.message : 'Could not reach the server');

export function useLabSetups<P>(
    labId: L.LabId, payloadVersion: number, saving: LabSaving<P> | undefined,
    /** Which bench inside the lab — the chemistry bench with amounts is
     *  'quantitative'. Its list holds that bench's setups only, and its saves
     *  are made for it. */
    labRef?: string,
): LabSetupsState<P> {
    const client = saving?.client;
    const notify = saving?.onCurrentChange;
    const [mine, setMine] = useState<L.LabSetupSummary[]>([]);
    const [current, setCurrent] = useState<CurrentSetup<P> | null>(() => (saving?.opened
        ? { id: saving.opened.id, name: saving.opened.name, notes: saving.opened.notes, chapterIds: saving.opened.chapterIds, saved: saving.opened.payload }
        : null));
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const refresh = useCallback(async () => {
        if (!client) return;
        try {
            // Filtered here rather than by the server: "no labRef" is not a filter it can be asked for.
            setMine((await client.list({ labId })).filter(s => (s.labRef ?? '') === (labRef ?? '')));
        } catch {
            // The list is a convenience beside the bench; the bench works without it.
        }
    }, [client, labId, labRef]);
    useEffect(() => { void refresh(); }, [refresh]);

    const save = useCallback(async (req: SaveRequest<P>): Promise<boolean> => {
        if (!client) return false;
        setBusy(true);
        setError(null);
        try {
            const doc = current && !req.asNew
                ? await client.update(current.id, {
                    payload: req.payload, payloadVersion,
                    ...(req.name !== undefined ? { name: req.name } : {}),
                    ...(req.notes !== undefined ? { notes: req.notes } : {}),
                    ...(req.chapterIds !== undefined ? { chapterIds: req.chapterIds } : {}),
                })
                : await client.create({
                    labId, payloadVersion, payload: req.payload,
                    ...(labRef ? { labRef } : {}),
                    name: req.name ?? 'Untitled setup',
                    ...(req.notes ? { notes: req.notes } : {}),
                    // Sent even when empty: no chapters is a choice, where absent is none made.
                    ...(req.chapterIds ? { chapterIds: req.chapterIds } : {}),
                    ...(req.basedOn ? { basedOn: req.basedOn } : current ? { basedOn: { setupId: current.id } } : {}),
                });
            setCurrent({ id: doc.id, name: doc.name, notes: doc.notes, chapterIds: doc.chapterIds, saved: req.payload });
            notify?.(doc.id);
            void refresh();
            return true;
        } catch (err) {
            setError(message(err));
            return false;
        } finally {
            setBusy(false);
        }
    }, [client, current, labId, labRef, payloadVersion, notify, refresh]);

    const load = useCallback(async (id: string): Promise<P | null> => {
        if (!client) return null;
        setBusy(true);
        setError(null);
        try {
            const doc = await client.get(id) as L.LabSetupDoc<P>;
            setCurrent({ id: doc.id, name: doc.name, notes: doc.notes, chapterIds: doc.chapterIds, saved: doc.payload });
            notify?.(doc.id);
            return doc.payload;
        } catch (err) {
            setError(message(err));
            return null;
        } finally {
            setBusy(false);
        }
    }, [client, notify]);

    const leave = useCallback(() => {
        if (!current) return;
        setCurrent(null);
        notify?.(null);
    }, [current, notify]);

    return { enabled: !!client, mine, current, busy, error, save, load, leave };
}
